import { useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';

const STORAGE_KEY = 'nestwise_seen_answer_sheet_tip_v1';

/**
 * One-time gentle nudge shown inside the Materials & Printables panel,
 * only on the first time a parent encounters a lesson with a worked-example
 * answer sheet. Dismissible — never shown again once dismissed or on its 4th view.
 */
export default function AnswerSheetTipBanner({ hasAnswerSheet }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!hasAnswerSheet) return;
    const state = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!state) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ views: 1, dismissed: false }));
      setShow(true);
      return;
    }
    if (state.dismissed) return;
    if (state.views >= 3) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, dismissed: true }));
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, views: state.views + 1 }));
    setShow(true);
  }, [hasAnswerSheet]);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ views: 99, dismissed: true }));
    setShow(false);
  };

  return (
    <div
      data-testid="answer-sheet-tip-banner"
      className="relative mb-4 rounded-2xl border-2 border-kowhai/40 bg-gradient-to-br from-kowhai-50 via-white to-kowhai-50/60 p-4 pr-10 shadow-sm animate-fadeInUp"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="dismiss-answer-sheet-tip"
        aria-label="Dismiss this tip"
        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-kowhai/20 hover:text-kowhai-dark transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-kowhai/20 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-kowhai-dark" />
        </div>
        <div className="flex-1">
          <p className="font-heading text-sm font-bold text-fern-dark mb-0.5">
            New: worked examples for parents
          </p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Every template below comes with a <span className="font-semibold text-kowhai-dark">completed &ldquo;worked example&rdquo;</span> you can download.
            Show it to your tamaiti if they&apos;re stuck — or study it yourself first so you can explain what to do.
          </p>
        </div>
      </div>
    </div>
  );
}
